import heroBackgroundDark from '/public/images/hero-background-dark.jpg';
import heroBackgroundLight from '/public/images/hero-background-light.jpg';
import api, {queryClient} from '@devrel-stack/api';
import {Layout, Newsletter} from '@devrel-stack/components';
import {
	defaultSEOConfig,
	fetchCurrentUser,
	fetchSubscribersCount,
	subscribeCustomer
} from '@devrel-stack/utilities';
import {dehydrate, useQuery} from '@tanstack/react-query';
import {GuidesGrid} from 'components/pages/index';
import {Loader} from 'components/ui';
import {
	GetStaticPaths,
	GetStaticProps,
	GetStaticPropsContext,
	NextPage
} from 'next';
import {BlogJsonLd, DefaultSeo, NextSeo} from 'next-seo';
import {useTheme} from 'next-themes';
import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import tw from 'twin.macro';

const CategoriesListingPage: NextPage = () => {
	const router = useRouter();
	const {data: site} = useQuery(['site'], () =>
		api.site.findOne({slug: 'guides'})
	);
	const {data: guides} = useQuery(['guides', router.query.slug], () =>
		api.guide.findMany({
			filter: `category=${router.query.slug}`
		})
	);
	const {data: category} = useQuery(['category', router.query.slug], () =>
		api.category.findOne({
			slug: router.query.slug as string
		})
	);

	const {theme} = useTheme();
	const [isMounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	if (router.isFallback) {
		return <Loader />;
	}

	return (
		<>
			{site && (
				<>
					<NextSeo
						title={category?.name}
						description={category?.excerpt}
						canonical={`${site.slug}${router.asPath}`}
						openGraph={{
							type: site.type,
							locale: site.locale,
							url: `${site.slug}${router.asPath}`,
							site_name: site.name,
							title: category?.name,
							description: category?.excerpt,
							images: defaultSEOConfig(site).openGraph.images
						}}
						twitter={{
							site: `@${site.twitterHandle}`,
							cardType: 'summary_large_image'
						}}
					/>
					<BlogJsonLd
						url={`${site.slug}${router.asPath}`}
						title={category?.name || ''}
						description={category?.excerpt || ''}
						images={[
							defaultSEOConfig(site).openGraph.images[0].url
						]}
						datePublished={new Date().toDateString()}
						dateModified={new Date().toDateString()}
						authorName="Rapid"
					/>
				</>
			)}
			<Layout logoText="API Guides" appUrl="guides">
				<div tw="space-y-0">
					<div
						tw="space-y-0 text-white bg-black"
						css={{
							backgroundColor: 'hsla(0,100%,50%,0)',
							backgroundImage: isMounted
								? theme === 'dark'
									? `url(${heroBackgroundDark.src})`
									: `url(${heroBackgroundLight.src})`
								: 'none',
							backgroundSize: 'cover',
							backgroundPosition: 'bottom'
						}}
					/>
					<div tw="relative">
						<div tw="mx-auto p-4">
							<div tw="py-32 space-y-4 mx-auto flex flex-col h-full items-center text-center">
								<div tw="rounded-full border border-purple-700 dark:border-purple-400 py-2 px-4 mb-2 max-w-max text-purple-700 dark:text-purple-400">
									{guides?.length} Guides
								</div>
								<h1 tw="text-4xl md:text-6xl bg-gradient-to-r from-purple-600 via-purple-500 to-purple-300 dark:from-purple-600 dark:via-purple-500 dark:to-purple-300 bg-clip-text text-transparent uppercase tracking-[-1px]">
									{category?.name}
								</h1>
								<p tw="text-lg text-black dark:text-white max-w-xl">
									{category?.excerpt}
								</p>
							</div>
						</div>
						<div
							css={[
								tw`absolute w-full h-[200px] z-[-1] top-48`,
								{
									background:
										theme === 'light'
											? 'linear-gradient(180deg,#a78bfa,#e7dcff 27.34%)'
											: 'linear-gradient(180deg,#5718e04d,#a78bfa50 27%)',
									filter: 'blur(100px)'
								}
							]}
						/>
						<GuidesGrid guides={guides} showInlineAvatars />
					</div>
					<Newsletter
						subscribeCustomer={subscribeCustomer}
						fetchSubscribersCount={fetchSubscribersCount}
					/>
				</div>
			</Layout>
		</>
	);
};

export const getStaticProps: GetStaticProps = async ({
	params
}: GetStaticPropsContext) => {
	const categorySlug = params?.slug;

	if (!categorySlug || Array.isArray(categorySlug)) {
		return {
			notFound: true
		};
	}

	await Promise.allSettled([
		queryClient.prefetchQuery(['guides', categorySlug], () =>
			api.guide.findMany({
				filter: `category=${categorySlug}`
			})
		),
		queryClient.prefetchQuery(['site'], () =>
			api.site.findOne({slug: 'guides'})
		),
		queryClient.prefetchQuery(['category', categorySlug], () =>
			api.category.findOne({
				slug: categorySlug
			})
		),
		queryClient.prefetchQuery(['user'], () => fetchCurrentUser('guides'))
	]);

	return {
		props: {
			dehydratedState: dehydrate(queryClient)
		}
	};
};

export const getStaticPaths: GetStaticPaths = async () => {
	const categories = await api.category.findMany({});

	return {
		paths: categories.slice(0, 10).map(category => {
			return {
				params: {
					slug: category.slug
				}
			};
		}),
		fallback: true
	};
};

export default CategoriesListingPage;
